import {
	Box,
	Button,
	Container,
	Divider,
	Link,
	Skeleton,
	TextField,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
	downloadFileRequest,
	getProductTypeRequest,
	getSkuRequest,
} from 'redux/reducers/gpsr/reducer';
import { useDebounce } from 'use-debounce';

import {
	getProductType,
	getProductTypeError,
	getProductTypeIsLoad,
	getSku,
	getSkuIsLoad,
} from 'redux/reducers/gpsr/selectors';
import queryString from 'query-string';
import { NotFoundPage } from 'pages/404';
import { NotFound } from 'components/NotFound';
import { Notice } from 'ui/Notice';
import { WarningAlert } from 'ui/WarningAlert';
import { getIsAuth } from 'redux/reducers/auth/selectors';
import { ReCaptchaPage } from 'pages/ReCAPTCHA';

export const ProductType = () => {
	const params = useParams<{ productTypeId?: string }>();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const productType = useSelector(getProductType);
	const productTypeLoading = useSelector(getProductTypeIsLoad);
	const productTypeError = useSelector(getProductTypeError);
	const sku = useSelector(getSku);
	const skuLoading = useSelector(getSkuIsLoad);

	const [skuSearch, setSkuSearch] = useState('');
	const [debouncedSkuSearch] = useDebounce(skuSearch, 500);

	useEffect(() => {
		if (debouncedSkuSearch.length < 3) return;
		if (!params?.productTypeId) return;

		dispatch(
			getSkuRequest({
				productTypeId: params.productTypeId,
				body: {
					sku: debouncedSkuSearch,
				},
			}),
		);
	}, [debouncedSkuSearch, dispatch, params?.productTypeId]);

	// const handleSkuSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
	// 	setSkuSearch(event.target.value);

	// 	if (!params?.productTypeId) return;
	// 	if (event.target.value.length < 3) return;

	// 	dispatch(
	// 		getSkuRequest({
	// 			productTypeId: params.productTypeId,
	// 			body: {
	// 				sku: event.target.value,
	// 			},
	// 		}),
	// 	);
	// };

	useEffect(() => {
		dispatch(
			getProductTypeRequest({
				productTypeId: params?.productTypeId ?? '0',
			}),
		);
	}, [dispatch, navigate, params?.productTypeId]);

	// useEffect(() => {
	// 	if (productTypeLoading) return;
	// 	if (productTypeError) {
	// 		navigate(`/404`);
	// 	}
	// }, [productType, navigate, productTypeLoading, productTypeError]);

	const handleDownloadFile = () => {
		if (!params?.productTypeId) return;
		dispatch(
			downloadFileRequest({
				productTypeId: params?.productTypeId ?? '0',
			}),
		);
	};

	if (productTypeError) {
		return <NotFound />;
	}

	return (
		<Container style={{ marginBottom: '20px' }}>
			{productTypeLoading && (
				<Skeleton
					variant="rectangular"
					height={300}
					sx={{
						marginBottom: '20px',
					}}
				/>
			)}
			{!productTypeLoading && productType && (
				<>
					<Typography
						variant="h4"
						component="h1"
						sx={{
							fontWeight: 600,
						}}
					>
						{productType?.productTypeName}
					</Typography>
					<Typography variant="body1" component="p" sx={{ marginTop: '10px' }}>
						{productType?.productTypeDescription.split('\n').map((line, index) => (
							// eslint-disable-next-line react/no-array-index-key
							<span key={index}>
								{line}
								<br />
							</span>
						))}
					</Typography>
					<Box sx={{ marginTop: '10px', display: 'flex', flexDirection: 'column' }}>
						<Typography
							variant="body1"
							component="h3"
							sx={{
								fontWeight: 600,
								marginBottom: '5px',
							}}
						>
							Regulations:
						</Typography>
						{productType?.regulations.map((regulation) => (
							<Typography key={regulation.name} variant="body1" component="p">
								{regulation.name}
							</Typography>
						))}
					</Box>
					<Box sx={{ marginTop: '10px', display: 'flex', flexDirection: 'column' }}>
						<Typography
							variant="body1"
							component="h3"
							sx={{
								fontWeight: 600,
							}}
						>
							Testing Information:
						</Typography>
						<Typography variant="body1" component="p" sx={{ marginTop: '5px' }}>
							{productType?.testingInformation.split('\n').map((line, index) => (
								// eslint-disable-next-line react/no-array-index-key
								<span key={index}>
									{line}
									<br />
								</span>
							))}
						</Typography>
					</Box>

					<Button
						variant="contained"
						color="primary"
						sx={{ marginTop: '20px', width: '360px' }}
						onClick={handleDownloadFile}
					>
						Download Declaration of Conformity
					</Button>
					<Divider
						sx={{
							marginTop: '20px',
							border: '1px solid #4181a9',
						}}
					/>
					<TextField
						id="outlined-multiline-static"
						label="Search for SKUs"
						variant="outlined"
						size="small"
						sx={{ marginTop: '20px', marginBottom: '10px', width: '360px' }}
						value={skuSearch}
						onChange={(e) => setSkuSearch(e.target.value)}
						helperText={
							debouncedSkuSearch.length < 3 && debouncedSkuSearch.length > 0
								? 'Please enter at least 3 characters'
								: debouncedSkuSearch.length > 0 && (sku?.length ?? 0) === 0
									? 'SKU Not Found'
									: ''
						}
						error={
							(debouncedSkuSearch.length < 3 && debouncedSkuSearch.length > 0) ||
							(debouncedSkuSearch.length > 0 && (sku?.length ?? 0) === 0)
						}
					/>
					{skuLoading && (
						<Skeleton
							variant="rectangular"
							height={100}
							sx={{
								marginBottom: '20px',
							}}
						/>
					)}
					{!skuLoading && (sku?.length ?? 0) > 0 && debouncedSkuSearch.length > 2 && (
						<Box
							sx={{
								marginBottom: '20px',
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							{sku?.map((skuItem) => (
								<Typography key={skuItem.sku} variant="body1" component="p">
									{skuItem.sku}
								</Typography>
							))}
						</Box>
					)}
					{!skuLoading && (sku?.length ?? 0) === 0 && debouncedSkuSearch.length > 2 && (
						<WarningAlert>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'start',
									gap: '30px',
								}}
							>
								<Typography
									color="error"
									sx={{
										fontSize: '6rem',
										fontStyle: 'italic',
										fontWeight: 'bold',
										textTransform: 'lowercase',
										mt: '-2rem',
										color: '#a9454d',
									}}
								>
									!
								</Typography>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										color: '#a9454d',
									}}
								>
									<Typography
										sx={{
											fontSize: '1.2rem',
											fontStyle: 'italic',
											fontWeight: 'bold',
											mb: '10px',
										}}
									>
										The SKU you entered could not be located in our database
									</Typography>
									<Typography>Possible Reasons:</Typography>
									<Box sx={{ pl: '20px' }}>
										<Typography>
											the SKU may not be registered under the Declaration of Conformity;
										</Typography>
										<Typography>there may be a typo or incorrect SKU entry;</Typography>
										<Typography>the product might still be in the registration process.</Typography>
									</Box>

									<Typography sx={{ fontSize: '1rem', fontStyle: 'italic', mt: '10px' }}>
										Next Steps:
									</Typography>
									<Box sx={{ pl: '20px' }}>
										<Typography>double-check the SKU for accuracy and try again;</Typography>
										<Typography>
											if the issue persists, contact the product manufacturer or Authorized
											Representative.
										</Typography>
									</Box>
									<Typography sx={{ fontSize: '1rem', fontStyle: 'italic', mt: '10px' }}>
										For further assistance, reach out to EAS Compliance at{' '}
										<Link
											href="mailto:gpsr@easproject.com"
											sx={{
												fontWeight: 600,
												textDecoration: 'none',
												color: '#a9454d',
											}}
										>
											gpsr@easproject.com
										</Link>
										.
									</Typography>
								</Box>
							</Box>
						</WarningAlert>
					)}
					{/* {debouncedSkuSearch.length < 3 && (
						<Typography variant="body1" component="p">
							Please enter at least 3 characters
						</Typography>
					)} */}
				</>
			)}
		</Container>
	);
};
